/**
 * Shared helpers and small components for the Paper Trading page.
 * Moved from PaperTradingPanel/PaperTradingPanel.tsx.
 */

import { useState, useEffect } from 'react';
import { useSessionEquity } from '../../hooks/usePaperTrading';
import type { PaperSession } from '../../types';

// ============================================================================
// Config display name helper
// ============================================================================

export function configDisplayName(session: Pick<PaperSession, 'aggregationConfig' | 'aggregationConfigId'>): string {
  const subs = session.aggregationConfig?.subStrategies;
  if (subs && subs.length > 0) {
    if (subs.length === 1) {
      const s = subs[0];
      const sym = s.symbol.replace('/USDT:USDT', '').replace('/USDT', '');
      return `${s.strategyName} on ${sym} ${s.timeframe}`;
    }
    const mode = session.aggregationConfig?.allocationMode ?? 'multi';
    return `${subs.length} strategies (${mode})`;
  }
  return session.aggregationConfigId ?? 'Manual config';
}

// ============================================================================
// Status Badge
// ============================================================================

export function StatusBadge({ status }: { status: PaperSession['status'] }) {
  const configs: Record<
    PaperSession['status'],
    { dotClass: string; label: string; textClass: string }
  > = {
    running: {
      dotClass: 'bg-green-500 animate-pulse',
      label: 'Running',
      textClass: 'text-green-400',
    },
    paused: {
      dotClass: 'bg-yellow-500',
      label: 'Paused',
      textClass: 'text-yellow-400',
    },
    stopped: {
      dotClass: 'bg-gray-500',
      label: 'Stopped',
      textClass: 'text-gray-400',
    },
    error: {
      dotClass: 'bg-red-500',
      label: 'Error',
      textClass: 'text-red-400',
    },
  };

  const cfg = configs[status];
  return (
    <span className={`flex items-center gap-1.5 text-xs font-medium ${cfg.textClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
      {cfg.label}
    </span>
  );
}

// ============================================================================
// Return percentage helper
// ============================================================================

export function returnPercent(currentEquity: number, initialCapital: number): number {
  if (initialCapital === 0) return 0;
  return ((currentEquity - initialCapital) / initialCapital) * 100;
}

// ============================================================================
// Format helpers
// ============================================================================

export function fmtUsd(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtPct(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export function fmtDate(ts: number | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

export function fmtDuration(ms: number): string {
  if (ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

// ============================================================================
// Next tick countdown
// ============================================================================

export function NextTickCountdown({ nextTickAt }: { nextTickAt: number | null }) {
  const [remaining, setRemaining] = useState<string>('');

  useEffect(() => {
    if (!nextTickAt) {
      setRemaining('—');
      return;
    }

    const update = () => {
      const ms = nextTickAt - Date.now();
      if (ms <= 0) {
        setRemaining('any moment...');
      } else {
        setRemaining(fmtDuration(ms));
      }
    };

    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [nextTickAt]);

  return <span className="text-gray-300">{remaining}</span>;
}

// ============================================================================
// Connector type badge
// ============================================================================

const CONNECTOR_STYLES: Record<string, { label: string; className: string }> = {
  paper: { label: 'Paper', className: 'bg-gray-700 text-gray-300' },
  bybit: { label: 'LIVE', className: 'bg-red-900/70 text-red-300 border border-red-700' },
};

export function ConnectorBadge({ connectorType }: { connectorType: string }) {
  const style = CONNECTOR_STYLES[connectorType] ?? CONNECTOR_STYLES.paper;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase leading-tight ${style.className}`}>
      {style.label}
    </span>
  );
}

// ============================================================================
// SHA-72: shared "is this a LIVE (real-money) session" discriminator
// ============================================================================

/**
 * True when `connectorType` is a real-money trading connector (currently
 * only `'bybit'`) rather than the `'paper'` (simulated) connector. This is
 * the SAME discriminator already used by ConnectorBadge (labels it "LIVE")
 * and BybitBalancePanel (only renders the Bybit balance comparison for
 * these sessions) — kept here as one named, testable function so every
 * live/paper branch point (including the SHA-72 result-risk-banner
 * suppression) uses one source of truth instead of re-deriving it inline.
 */
export function isLiveConnector(connectorType: string): boolean {
  return connectorType === 'bybit';
}

// ============================================================================
// Session card (in the list)
// ============================================================================

export interface SessionCardProps {
  session: PaperSession;
  isSelected: boolean;
  onSelect: () => void;
}

export function SessionCard({ session, isSelected, onSelect }: SessionCardProps) {
  const liveEquity = useSessionEquity(session.id);
  const equity = liveEquity?.equity ?? session.currentEquity;
  const ret = liveEquity?.returnPct ?? returnPercent(session.currentEquity, session.initialCapital);
  const isPositive = ret >= 0;

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-900/20'
          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-white truncate">{session.name}</p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {configDisplayName(session)}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <ConnectorBadge connectorType={session.connectorType} />
          <StatusBadge status={session.status} />
        </div>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm text-gray-300">{fmtUsd(equity)}</span>
        <span className={`text-sm font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {fmtPct(ret)}
        </span>
      </div>
    </button>
  );
}
